import React, {
  SyntheticEvent, useEffect, useState, useCallback,
} from 'react';
import {
  Box,
  Card,
  IconButton,
  Modal,
  Typography,
  TextField,
  Button,
  MenuItem,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  DescriptionOutlined as DescriptionIcon,
} from '@mui/icons-material';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { CustomDateTextField } from '../../../components/General/CustomTextfields';
import { getPrograms } from '../../../requests/api/sections';
import handleApiResponse from '../../../utils/handleApiResponse';
import { AddProgramModalProps, ProgramModalData } from './types';
import useStyles from './styles';

function AddProgramModal({
  modalOpen,
  setModalOpen,
  onAddProgram,
  editingProgram,
  defaultYear,
  subjects,
}: AddProgramModalProps) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  type ProgramOption = {
    id: string;
    name: string;
    startDate?: Date | null;
    endDate?: Date | null;
    fee?: number;
  };

  const [programs, setPrograms] = useState<ProgramOption[]>([]);
  const resolvedDefaultYear = defaultYear ?? new Date().getFullYear();

  const getInitialValues = useCallback((): ProgramModalData => ({
    subjectId: '',
    subjectName: '',
    year: resolvedDefaultYear,
    level: '',
    classYear: '',
    programId: '',
    programName: '',
    startDate: new Date(),
    endDate: new Date(),
    originalFee: '0',
    discount: '0',
    comment: '',
  }), [resolvedDefaultYear]);

  const [modalValues, setModalValues] = useState<ProgramModalData>(getInitialValues);

  const isEditing = !!editingProgram;

  useEffect(() => {
    if (modalOpen) {
      if (editingProgram) {
        setModalValues(editingProgram);
      } else {
        setModalValues(getInitialValues());
      }
    }
  }, [modalOpen, editingProgram, getInitialValues]);

  useEffect(() => {
    const fetchPrograms = async () => {
      if (modalValues.subjectId) {
        try {
          const { programs: programsData } = await getPrograms(
            modalValues.subjectId,
            modalValues.year,
            modalValues.level,
            modalValues.classYear,
          );
          setPrograms(programsData.map((p: {
            id: string;
            name: string;
            startDate?: string;
            endDate?: string;
            fee?: number | string;
          }) => {
            const feeValue = Number(p.fee);
            return {
              id: p.id,
              name: p.name,
              startDate: p.startDate ? new Date(p.startDate) : null,
              endDate: p.endDate ? new Date(p.endDate) : null,
              fee: Number.isNaN(feeValue) ? undefined : feeValue,
            };
          }));
        } catch (err) {
          const e = err as AxiosError;
          handleApiResponse(enqueueSnackbar, e, false);
        }
      } else {
        setPrograms([]);
        setModalValues((prev) => ({ ...prev, programId: '' }));
      }
    };

    if (modalOpen && modalValues.subjectId) {
      fetchPrograms();
    }
  }, [
    modalOpen,
    modalValues.subjectId,
    modalValues.year,
    modalValues.level,
    modalValues.classYear,
  ]);

  const sanitizeCurrencyValue = (rawValue: string) => rawValue.replace(/\D/g, '');
  const parseCurrencyValue = (value: string) => Number(value || '0');
  const formatCurrencyDisplay = (value: string) => {
    if (!value) {
      return '';
    }
    const numericValue = parseInt(value, 10);
    if (Number.isNaN(numericValue)) {
      return '';
    }
    return `$${numericValue.toLocaleString('es-CL')}`;
  };

  const handleFieldChange = (
    e: React.ChangeEvent<HTMLInputElement> | SyntheticEvent<Element, Event> | Date,
    source: string,
  ) => {
    if (e instanceof Date) {
      setModalValues((prevState) => ({ ...prevState, [source]: e }));
    } else if (!(e instanceof File)) {
      setModalValues(
        (prevState) => ({
          ...prevState,
          [source]: (e.target as HTMLInputElement).value,
        }),
      );
    }
  };

  const handleCurrencyChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: 'originalFee' | 'discount',
  ) => {
    const numericValue = sanitizeCurrencyValue(e.target.value);
    setModalValues((prevState) => ({
      ...prevState,
      [field]: numericValue || '0',
    }));
  };

  const validateProgramValues = () => {
    const originalFeeValue = parseCurrencyValue(modalValues.originalFee);
    const discountValue = parseCurrencyValue(modalValues.discount);

    if (Number.isNaN(originalFeeValue) || originalFeeValue < 0) {
      enqueueSnackbar('El arancel debe ser un número igual o mayor a 0', { variant: 'error' });
      return false;
    }

    if (Number.isNaN(discountValue) || discountValue < 0) {
      enqueueSnackbar('El descuento debe ser un número igual o mayor a 0', { variant: 'error' });
      return false;
    }

    if (discountValue > originalFeeValue) {
      enqueueSnackbar('El descuento no puede ser mayor al arancel', { variant: 'error' });
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (!modalValues.programId) {
      return;
    }

    const selectedProgram = programs.find((program) => program.id === modalValues.programId);
    if (!selectedProgram) {
      return;
    }

    setModalValues((prevState) => ({
      ...prevState,
      startDate: selectedProgram.startDate || prevState.startDate,
      endDate: selectedProgram.endDate || prevState.endDate,
      originalFee: selectedProgram.fee !== undefined
        ? sanitizeCurrencyValue(String(selectedProgram.fee))
        : prevState.originalFee,
    }));
  }, [modalValues.programId, programs]);

  const handleClose = () => {
    setModalValues(getInitialValues());
    setPrograms([]);
    setModalOpen(false);
  };

  const handleAdd = () => {
    if (!modalValues.subjectId || !modalValues.programId) {
      enqueueSnackbar('Debe seleccionar una asignatura y un programa', {
        variant: 'error',
      });
      return;
    }

    if (!validateProgramValues()) {
      return;
    }

    const subjectName = subjects.find((s) => s.id === modalValues.subjectId)?.name || '';
    const programName = programs.find((p) => p.id === modalValues.programId)?.name || '';
    const originalFeeValue = String(parseCurrencyValue(modalValues.originalFee));
    const discountValue = String(parseCurrencyValue(modalValues.discount));

    onAddProgram({
      ...modalValues,
      subjectName,
      programName,
      originalFee: originalFeeValue,
      discount: discountValue,
    });
    handleClose();
  };

  return (
    <Modal
      open={modalOpen}
      onClose={handleClose}
    >
      <Box className={classes.modalContainer}>
        <Card className={classes.modal}>
          <Box className={classes.modalContent}>
            <Box className={classes.modalTitleContainer}>
              <Box className={classes.modalTitleAndIcon}>
                <DescriptionIcon className={classes.modalTitleIcon} color="primary" />
                <Typography className={classes.modalTitle}>
                  {isEditing ? 'Editar programa' : 'Agregar programa'}
                </Typography>
              </Box>
              <IconButton onClick={handleClose}>
                <CloseIcon className={classes.modalTitleIcon} color="primary" />
              </IconButton>
            </Box>
            <Box className={classes.modalTextfieldsContainer}>
              <Box style={{ width: 'clamp(300px, 39.06vw, 800px)' }}>
                <TextField
                  fullWidth
                  label="Año"
                  variant="outlined"
                  value={modalValues.year}
                  onChange={(e) => handleFieldChange(e, 'year')}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ inputMode: 'numeric' }}
                  type="number"
                  inputProps={{ min: 2024, max: new Date().getFullYear() + 1 }}
                  disabled={!!editingProgram}
                />
              </Box>
              <Box className={classes.modalIndividualTextfield}>
                <TextField
                  select
                  fullWidth
                  label="Asignatura"
                  variant="outlined"
                  value={modalValues.subjectId}
                  onChange={(e) => handleFieldChange(e, 'subjectId')}
                  InputLabelProps={{ shrink: true }}
                  disabled={!!editingProgram}
                >
                  {subjects.map((subject) => (
                    <MenuItem key={subject.id} value={subject.id}>
                      {subject.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box className={classes.modalIndividualTextfield}>
                <TextField
                  select
                  fullWidth
                  label="Nivel"
                  variant="outlined"
                  value={modalValues.level}
                  onChange={(e) => handleFieldChange(e, 'level')}
                  InputLabelProps={{ shrink: true }}
                  disabled={!!editingProgram}
                >
                  <MenuItem value="">Seleccionar nivel</MenuItem>
                  <MenuItem value="Normal">Normal</MenuItem>
                  <MenuItem value="Intermedio">Intermedio</MenuItem>
                  <MenuItem value="Avanzado">Avanzado</MenuItem>
                </TextField>
              </Box>
              <Box className={classes.modalIndividualTextfield}>
                <TextField
                  select
                  fullWidth
                  label="Curso"
                  variant="outlined"
                  value={modalValues.classYear}
                  onChange={(e) => handleFieldChange(e, 'classYear')}
                  InputLabelProps={{ shrink: true }}
                  disabled={!!editingProgram}
                >
                  <MenuItem value="">Seleccionar curso</MenuItem>
                  <MenuItem value="III Medio">III Medio</MenuItem>
                  <MenuItem value="IV Medio">IV Medio</MenuItem>
                  <MenuItem value="Egresados">Egresados</MenuItem>
                </TextField>
              </Box>
              <Box className={classes.modalIndividualTextfield}>
                <TextField
                  select
                  fullWidth
                  label="Programa"
                  variant="outlined"
                  value={modalValues.programId}
                  onChange={(e) => handleFieldChange(e, 'programId')}
                  disabled={!modalValues.subjectId || !!editingProgram}
                  InputLabelProps={{ shrink: true }}
                >
                  {programs.length === 0
                    ? <MenuItem value="">No hay programas disponibles</MenuItem>
                    : (
                      programs.map((program) => (
                        <MenuItem key={program.id} value={program.id}>
                          {program.name}
                        </MenuItem>
                      ))
                    )}
                </TextField>
              </Box>
              <Box className={classes.modalIndividualTextfield}>
                <CustomDateTextField
                  keyField="startDate"
                  name="Fecha de inicio"
                  value={modalValues.startDate}
                  onChangeFunction={handleFieldChange}
                  disabled
                />
              </Box>
              <Box className={classes.modalIndividualTextfield}>
                <CustomDateTextField
                  keyField="endDate"
                  name="Fecha de término"
                  value={modalValues.endDate}
                  onChangeFunction={handleFieldChange}
                  disabled
                />
              </Box>
              <Box className={classes.modalIndividualTextfield}>
                <TextField
                  fullWidth
                  label="Arancel"
                  variant="outlined"
                  value={formatCurrencyDisplay(modalValues.originalFee)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ inputMode: 'numeric', readOnly: true }}
                  disabled
                />
              </Box>
              <Box className={classes.modalIndividualTextfield}>
                <TextField
                  fullWidth
                  label="Descuento"
                  variant="outlined"
                  value={formatCurrencyDisplay(modalValues.discount)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleCurrencyChange(e, 'discount')}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ inputMode: 'numeric' }}
                />
              </Box>
              <Box style={{ width: 'clamp(300px, 39.06vw, 800px)' }}>
                <TextField
                  key="comment"
                  fullWidth
                  label="Comentario (opcional)"
                  variant="outlined"
                  value={modalValues.comment}
                  multiline
                  minRows={3}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setModalValues((prevState) => ({
                      ...prevState,
                      comment: e.target.value,
                    }));
                  }}
                />
              </Box>
            </Box>
            <Box className={classes.modalButtonsContainer}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAdd}
                startIcon={<CheckIcon />}
                className={classes.modalAddButton}
              >
                {isEditing ? 'Guardar cambios' : 'Agregar Programa'}
              </Button>
            </Box>
          </Box>
        </Card>
      </Box>
    </Modal>
  );
}

export default AddProgramModal;
